import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { filterMenuStructure } from '../../config/menuConfig';
import { authenticatedApiCall } from '../../utils/apiClient';
import { MENU_STRUCTURE } from '../../config/menuStructure';
import { useResponsive } from '../../hooks/useResponsive';
import { getMyFeatureFlags } from '../../utils/featureFlags';
import { canSeeMenuItem as canSeeMenuItemShared } from '../../utils/menuAccess';
import * as portalSession from '../../auth/portalSession';
import DepartmentDropdown from './DepartmentDropdown';

const ProjectSidebar = React.memo(({ activeMenuItem, onMenuItemClick, isCollapsed, onToggleCollapse, isMobileOpen, onMobileClose, projectId, selectedDepartment = 'projects', onSelectDepartment, selectedEstimateId = null, selectedEstimateNumber = null, jobNumber = null, enabledScheduleModules = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminActive = location.pathname === '/admin';
  const { isMobile, isTablet, isTouchDevice } = useResponsive();
  const { instance, accounts } = useMsal();
  // E-mail-code guests have no Microsoft account. The portal session is the login.
  const hasLogin = !!(accounts[0] || portalSession.isActive());
  const signedInEmail = accounts[0]?.username || portalSession.current()?.user?.email || '';
  const [expandedSections, setExpandedSections] = useState({});
  const [userPermissions, setUserPermissions] = useState(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  // Feature flags for staged rollouts (null until loaded — gated items stay hidden)
  const [featureFlags, setFeatureFlags] = useState(null);
  // Optional schedule modules (LBS, …) — opt-in per estimate/job
  const [scheduleModules, setScheduleModules] = useState(enabledScheduleModules || {});

  useEffect(() => {
    if (hasLogin) {
      getMyFeatureFlags(instance, accounts).then(setFeatureFlags);
    }
  }, [instance, accounts, hasLogin]);

  useEffect(() => {
    if (enabledScheduleModules && typeof enabledScheduleModules === 'object') {
      setScheduleModules(enabledScheduleModules);
    }
  }, [enabledScheduleModules]);

  useEffect(() => {
    let cancelled = false;
    const scopeType = selectedEstimateNumber ? 'estimate' : (jobNumber ? 'job' : null);
    const scopeKey = selectedEstimateNumber || jobNumber || null;
    if (!scopeType || !scopeKey || !hasLogin) {
      if (!enabledScheduleModules) setScheduleModules({});
      return undefined;
    }
    (async () => {
      try {
        const res = await authenticatedApiCall(
          instance,
          accounts,
          'GET',
          `/schedule-modules/${scopeType}/${encodeURIComponent(scopeKey)}`
        );
        if (!cancelled) setScheduleModules(res.data?.data?.enabled || {});
      } catch (e) {
        if (!cancelled) setScheduleModules({});
      }
    })();
    const onChanged = (ev) => {
      const d = ev?.detail;
      if (!d || d.scopeType !== scopeType) return;
      if (String(d.scopeKey) !== String(scopeKey)) return;
      setScheduleModules(d.enabled || {});
    };
    window.addEventListener('schedule-modules-changed', onChanged);
    return () => {
      cancelled = true;
      window.removeEventListener('schedule-modules-changed', onChanged);
    };
  }, [instance, accounts, hasLogin, selectedEstimateNumber, jobNumber, enabledScheduleModules]);
  
  // Hover flyout state for collapsed sidebar
  const [hoveredSection, setHoveredSection] = useState(null);
  const [flyoutPosition, setFlyoutPosition] = useState({ top: 0 });
  const hoverTimeoutRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  
  // Hover flyout state for 3rd level sub-items in expanded sidebar
  const [hoveredSubMenuItem, setHoveredSubMenuItem] = useState(null);
  const [subMenuFlyoutPos, setSubMenuFlyoutPos] = useState({ top: 0, left: 0 });
  const subMenuHoverTimeout = useRef(null);
  const subMenuItemRefs = useRef({});
  
  // Hover flyout state for 3rd level sub-items in collapsed sidebar flyout
  const [collapsedSubFlyout, setCollapsedSubFlyout] = useState(null);
  const collapsedFlyoutItemRefs = useRef({});
  const collapsedSubFlyoutTimeout = useRef(null);
  
  // Project selector state (when no project is selected)
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  
  // Resizable sidebar state
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    // Load saved width from localStorage or default to 220
    const saved = localStorage.getItem('sidebarWidth');
    return saved ? parseInt(saved, 10) : 220;
  });
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);
  
  // Min and max width constraints
  const MIN_WIDTH = 180;
  const MAX_WIDTH = 400;
  
  // Handle mouse down on resize handle
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);
  
  // Handle mouse move for resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const newWidth = e.clientX;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };
    
    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        // Save to localStorage
        localStorage.setItem('sidebarWidth', sidebarWidth.toString());
      }
    };
    
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      // Change cursor for entire document while resizing
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, sidebarWidth]);

  // Fetch user permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await authenticatedApiCall(
          instance,
          accounts,
          'GET',
          '/auth/permissions-info'
        );
        if (response.data.success) {
          setUserPermissions(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching permissions:', error);
        // Set minimal fallback permissions - core items (home, my-projects) will still show
        setUserPermissions({ 
          permissions: [], 
          menuItems: [],  // Important: must include this for canSeeMenuItem logic
          isElevated: false, 
          canAccessAdminPanel: false,
          isSuperadmin: false,
          isAdmin: false,
          allDepartmentIds: [],
          subDepartments: []
        });
      }
      setPermissionsLoaded(true);
    };
    if (hasLogin) {
      fetchPermissions();
    } else {
      // No account yet - still set permissionsLoaded to avoid stuck state
      // Menu will show core items only until login completes
      setPermissionsLoaded(true);
      setUserPermissions({ isSuperadmin: false, isAdmin: false, permissions: [], isElevated: false });
    }
  }, [instance, accounts, hasLogin]);

  // Fetch projects when no project is selected (for project selector in flyout)
  useEffect(() => {
    const fetchProjects = async () => {
      if (projectId || projects.length > 0) return; // Already have project or projects loaded
      
      try {
        setProjectsLoading(true);
        const response = await authenticatedApiCall(
          instance,
          accounts,
          'GET',
          '/projects'
        );
        if (response.data.projects) {
          // Sort by job number
          const sorted = [...response.data.projects].sort((a, b) => {
            const numA = parseInt(a.job_number, 10);
            const numB = parseInt(b.job_number, 10);
            if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
            return (a.job_number || '').localeCompare(b.job_number || '');
          });
          setProjects(sorted);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setProjectsLoading(false);
      }
    };
    
    if (hasLogin && !projectId) fetchProjects();
  }, [instance, accounts, hasLogin, projectId, projects.length]);
  
  // Filter projects based on search term
  const filteredProjects = useMemo(() => {
    if (!projectSearchTerm.trim()) return projects;
    const search = projectSearchTerm.toLowerCase();
    return projects.filter(p => 
      (p.job_number || '').toLowerCase().includes(search) ||
      (p.job_name || '').toLowerCase().includes(search)
    );
  }, [projects, projectSearchTerm]);

  // Listen for tutorial events to expand/collapse sidebar
  useEffect(() => {
    const handleExpandSidebar = () => {
      // Ensure sidebar is not collapsed during tutorial
      if (isCollapsed && onToggleCollapse) {
        onToggleCollapse();
      }
    };

    const handleExpandSection = (event) => {
      const { sectionId } = event.detail || {};
      if (sectionId) {
        // Expand the specified section
        setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
      }
    };

    const handleTutorialFinished = () => {
      // Collapse sidebar when tutorial ends
      if (!isCollapsed && onToggleCollapse) {
        onToggleCollapse();
      }
    };

    window.addEventListener('tutorial:expandSidebar', handleExpandSidebar);
    window.addEventListener('tutorial:expandSection', handleExpandSection);
    window.addEventListener('tutorial:finished', handleTutorialFinished);

    return () => {
      window.removeEventListener('tutorial:expandSidebar', handleExpandSidebar);
      window.removeEventListener('tutorial:expandSection', handleExpandSection);
      window.removeEventListener('tutorial:finished', handleTutorialFinished);
    };
  }, [isCollapsed, onToggleCollapse]);

  // Command palette / header menu search → reuse sidebar click handler
  const menuNavigateHandlerRef = useRef(null);
  useEffect(() => {
    const onNavigate = (event) => {
      const itemId = event.detail?.itemId;
      if (!itemId || !menuNavigateHandlerRef.current) return;
      const department = event.detail?.department;
      if (department && onSelectDepartment) {
        onSelectDepartment(department);
      }
      menuNavigateHandlerRef.current(itemId);
    };
    window.addEventListener('portal:navigateMenu', onNavigate);
    return () => window.removeEventListener('portal:navigateMenu', onNavigate);
  }, [onSelectDepartment]);

  /**
   * canSeeMenuItem - V2 Permission System Menu Visibility Check
   * 
   * Determines whether a user can see a specific menu item based on:
   * - Department access (RESOURCE_DEPARTMENTS in resourceAccess.js)
   * - Sub-department access (RESOURCE_SUB_DEPARTMENTS in resourceAccess.js)
   * 
   * Architecture:
   * - Departments control access to modules (Construction, Accounting, etc.)
   * - Sub-departments provide granular access within a department (Piping, Electrical, etc.)
   * - Authority levels control what users can DO inside a module (not visibility)
   * 
   * CURRENT STATE (Development Phase):
   * - Only core items visible: Home, Project Directory, Time Tracker, Admin
   * - In project context: Only Progress Tracking > Piping visible
   * - Superadmins see everything
   * 
   * Flow:
   * 1. Core items (home) - always visible
   * 2. Loading state - hide until permissions loaded
   * 3. Superadmin bypass - sees everything
   * 4. Allowed items list - only show items we've explicitly enabled
   * 5. Department access - user must have access to the required department
   * 6. Sub-department access - for granular module access (e.g., Progress Tracking disciplines)
   * 
   * @param {string} itemId - The menu item ID to check
   * @returns {boolean} - Whether the user can see this menu item
   */
  // Helper to check if an item belongs to a section that doesn't need a project
  // (i.e., has hideInProject: true on itself or any ancestor)
  const itemDoesNotNeedProject = useCallback((itemId) => {
    const findItem = (items, ancestorHasHideInProject = false) => {
      for (const item of items) {
        const currentHasHide = item.hideInProject === true || ancestorHasHideInProject;
        if (item.id === itemId) {
          return currentHasHide;
        }
        if (item.subItems) {
          const result = findItem(item.subItems, currentHasHide);
          if (result !== null) {
            return result;
          }
        }
      }
      return null; // Not found at this level
    };
    const result = findItem(MENU_STRUCTURE);
    return result === true;
  }, []);

  const canSeeMenuItem = useCallback((itemId) => canSeeMenuItemShared(itemId, {
    userPermissions,
    permissionsLoaded,
    featureFlags,
    userEmail: signedInEmail
  }), [userPermissions, permissionsLoaded, featureFlags, signedInEmail]);

  // Filter menu based on permissions, department, and project context
  // Parent sections are shown if user has permission for ANY of their children
  // Items with requiresProject: true are hidden when no project is selected
  // Items with hideInProject: true are hidden when a project IS selected
  // Superadmins bypass hideInProject restrictions
  const menuStructure = useMemo(() => {
    const envFilteredMenu = filterMenuStructure(MENU_STRUCTURE);
    const isSuperadmin = userPermissions?.isSuperadmin || userPermissions?.isAdmin;
    
    // First filter by department (only for top-level items with department property)
    const departmentFilteredMenu = envFilteredMenu.filter(item => {
      // If item has a department property, filter based on selected department
      if (item.department) {
        return item.department === selectedDepartment;
      }
      // Items without department property are always shown
      return true;
    });
    
    const filterByPermissions = (items) => {
      return items
        .map(item => {
          // Hide items that require a project when no project is selected
          if (item.requiresProject && !projectId) {
            return null;
          }
          
          // Hide items that require an estimate when no estimate is selected
          if (item.requiresEstimate && !selectedEstimateId) {
            return null;
          }

          // Optional schedule modules (e.g. LBS) — only show when enabled for this estimate/job
          if (item.requiresModule && !scheduleModules?.[item.requiresModule]) {
            return null;
          }
          
          // Hide items that should be hidden when in project context
          // Superadmins bypass this restriction
          if (item.hideInProject && projectId && !isSuperadmin) {
            return null;
          }

          if (item.requiresSuperadmin && !isSuperadmin) {
            return null;
          }
          
          // If item has children, check if any children are visible
          if (item.subItems && item.subItems.length > 0) {
            const filteredSubItems = filterByPermissions(item.subItems);
            if (filteredSubItems.length > 0) {
              // Parent is visible if any child is visible (don't require explicit parent permission)
              return { ...item, subItems: filteredSubItems };
            }
            // No visible children - hide parent unless user has explicit permission for parent
            if (canSeeMenuItem(item.id)) {
              return { ...item, subItems: [] };
            }
            return null;
          }
          // Leaf item - check permission directly
          if (!canSeeMenuItem(item.id)) return null;
          return item;
        })
        .filter(Boolean);
    };
    return filterByPermissions(departmentFilteredMenu);
  }, [canSeeMenuItem, projectId, selectedDepartment, userPermissions, selectedEstimateId, scheduleModules]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };
  
  // Refs for section elements to enable scrolling into view
  const sectionRefs = useRef({});
  
  // Click-based section expansion (removed hover-based expansion)
  // Sections now only expand/collapse when clicked
  
  // Hover handlers for collapsed sidebar flyout
  const handleCollapsedIconMouseEnter = (section, e) => {
    if (isMobile || isTablet) return;
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    // Get the position of the hovered icon
    const rect = e.currentTarget.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    
    // Estimate flyout height (header ~40px + items ~36px each + padding ~16px)
    const itemCount = section.subItems ? section.subItems.length : 0;
    // Extra space for project selector (~300px) when no project selected AND section needs project
    const needsProjectSelector = !projectId && !section.hideInProject;
    const estimatedFlyoutHeight = 40 + (itemCount * 36) + 16 + (needsProjectSelector ? 300 : 0);
    
    // Calculate optimal top position
    let optimalTop = rect.top;
    const flyoutBottom = optimalTop + estimatedFlyoutHeight;
    
    // If flyout would go off bottom of screen, adjust position
    if (flyoutBottom > viewportHeight - 20) {
      // Anchor to bottom of viewport with some padding
      optimalTop = Math.max(60, viewportHeight - estimatedFlyoutHeight - 20);
    }
    
    setFlyoutPosition({ top: optimalTop, maxHeight: viewportHeight - optimalTop - 20 });
    setHoveredSection(section);
  };
  
  const handleCollapsedIconMouseLeave = () => {
    if (isMobile || isTablet) return;
    // Delay closing to allow mouse to move to flyout (increased from 150ms for deeply nested menus)
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredSection(null);
    }, 350);
  };
  
  const handleFlyoutMouseEnter = () => {
    // Clear close timeout when mouse enters flyout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };
  
  const handleFlyoutMouseLeave = () => {
    // Close flyout when mouse leaves (increased from 150ms for deeply nested menus)
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredSection(null);
    }, 350);
  };
  
  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      if (collapsedSubFlyoutTimeout.current) clearTimeout(collapsedSubFlyoutTimeout.current);
    };
  }, []);
  
  // Close collapsed sub-flyout when main flyout closes
  useEffect(() => {
    if (!hoveredSection) {
      setCollapsedSubFlyout(null);
    }
  }, [hoveredSection]);

  const isItemActive = (itemId) => activeMenuItem === itemId;

  const isParentActive = (section) => {
    if (!section.subItems) return false;
    return section.subItems.some(item => {
      if (item.subItems) {
        return item.subItems.some(sub => isItemActive(sub.id)) || isItemActive(item.id);
      }
      return isItemActive(item.id);
    });
  };

  // Auto-expand section containing active item
  useEffect(() => {
    menuStructure.forEach(section => {
      if (isParentActive(section) && !expandedSections[section.id]) {
        setExpandedSections(prev => ({ ...prev, [section.id]: true }));
      }
    });
  }, [activeMenuItem]);

  const renderSubItems = (items, level = 1) => {
    return items.map(item => {
      const hasChildren = item.subItems && item.subItems.length > 0;
      // For department items, check if this department is selected
      const isDeptActive = item.departmentId && item.departmentId === selectedDepartment;
      const isActive = isDeptActive || isItemActive(item.id);
      const isExpanded = expandedSections[item.id];
      const showAsFlyout = hasChildren && level >= 1;
      const isFlyoutOpen = hoveredSubMenuItem === item.id;

      return (
        <div 
          key={item.id}
          ref={el => { if (showAsFlyout) subMenuItemRefs.current[item.id] = el; }}
          onMouseEnter={() => {
            if (showAsFlyout) {
              if (subMenuHoverTimeout.current) {
                clearTimeout(subMenuHoverTimeout.current);
                subMenuHoverTimeout.current = null;
              }
              const el = subMenuItemRefs.current[item.id];
              if (el) {
                const rect = el.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const itemCount = item.subItems.length;
                const estimatedHeight = 40 + itemCount * 36 + 16;
                let top = rect.top;
                if (top + estimatedHeight > viewportHeight - 10) {
                  top = Math.max(10, viewportHeight - estimatedHeight - 10);
                }
                setSubMenuFlyoutPos({ top, left: rect.right });
              }
              setHoveredSubMenuItem(item.id);
            }
          }}
          onMouseLeave={() => {
            if (showAsFlyout) {
              subMenuHoverTimeout.current = setTimeout(() => {
                setHoveredSubMenuItem(prev => prev === item.id ? null : prev);
              }, 250);
            }
          }}
        >
          <div
            data-tutorial={`menu-item-${item.id}`}
            onClick={() => {
              if (hasChildren && !showAsFlyout) {
                toggleSection(item.id);
              } else if (!hasChildren) {
                handleMenuItemClick(item.id);
              }
            }}
            style={{
              padding: `0.5rem 0.75rem 0.5rem ${0.75 + level * 0.75}rem`,
              cursor: hasChildren && showAsFlyout ? 'default' : 'pointer',
              backgroundColor: isActive ? '#dc3545' : (isFlyoutOpen ? 'rgba(255,255,255,0.15)' : 'transparent'),
              color: isActive ? 'white' : '#e0e0e0',
              fontSize: '0.85rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderLeft: isActive ? '3px solid #fff' : '3px solid transparent',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => {
              if (!isActive && !isFlyoutOpen) {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive && !isFlyoutOpen) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <span style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              flex: 1
            }}>
              {item.label}
              {item.isModal && <span style={{ fontSize: '0.7rem', marginLeft: '0.25rem' }}>⬈</span>}
            </span>
            {hasChildren && (
              <span style={{ 
                fontSize: '0.7rem',
                transform: showAsFlyout ? 'rotate(0deg)' : (isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'),
                transition: 'transform 0.2s'
              }}>▶</span>
            )}
          </div>
          {/* Fallback: inline expand for items without flyout */}
          {hasChildren && !showAsFlyout && isExpanded && (
            <div>{renderSubItems(item.subItems, level + 1)}</div>
          )}
        </div>
      );
    });
  };

  // State for hover-expanded flyout items
  const [hoveredFlyoutItems, setHoveredFlyoutItems] = useState({});
  const flyoutHoverTimeouts = useRef({});

  // Helper to clear all flyout hover timeouts
  const clearAllFlyoutTimeouts = () => {
    Object.keys(flyoutHoverTimeouts.current).forEach(key => {
      if (flyoutHoverTimeouts.current[key]) {
        clearTimeout(flyoutHoverTimeouts.current[key]);
        flyoutHoverTimeouts.current[key] = null;
      }
    });
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (collapsedSubFlyoutTimeout.current) {
      clearTimeout(collapsedSubFlyoutTimeout.current);
      collapsedSubFlyoutTimeout.current = null;
    }
  };

  // Render flyout items for collapsed sidebar hover menu
  // 3rd-level items appear as a sub-flyout to the right instead of inline
  const renderFlyoutItems = (items, level = 0, parentIds = [], doesNotNeedProject = false) => {
    return items.map(item => {
      const hasChildren = item.subItems && item.subItems.length > 0;
      const isActive = isItemActive(item.id);
      const isSubFlyoutOpen = collapsedSubFlyout?.itemId === item.id;

      return (
        <div 
          key={item.id}
          ref={el => { if (hasChildren) collapsedFlyoutItemRefs.current[item.id] = el; }}
          onMouseEnter={() => {
            clearAllFlyoutTimeouts();
            
            if (hasChildren) {
              const el = collapsedFlyoutItemRefs.current[item.id];
              if (el) {
                const rect = el.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const viewportWidth = window.innerWidth;
                const itemCount = item.subItems.length;
                const estimatedHeight = itemCount * 36 + 40;
                let top = rect.top;
                if (top + estimatedHeight > viewportHeight - 10) {
                  top = Math.max(10, viewportHeight - estimatedHeight - 10);
                }
                let left = rect.right + 2;
                if (left + 180 > viewportWidth) {
                  left = rect.left - 182;
                }
                setCollapsedSubFlyout({
                  itemId: item.id,
                  item: item,
                  top,
                  left,
                  doesNotNeedProject,
                });
              }
            } else {
              setCollapsedSubFlyout(null);
            }
          }}
          onMouseLeave={() => {
            if (hasChildren) {
              collapsedSubFlyoutTimeout.current = setTimeout(() => {
                setCollapsedSubFlyout(prev => prev?.itemId === item.id ? null : prev);
              }, 300);
            }
          }}
        >
          <div
            onClick={() => {
              if (!hasChildren) {
                handleMenuItemClick(item.id);
                setHoveredSection(null);
                setHoveredFlyoutItems({});
                setCollapsedSubFlyout(null);
              }
            }}
            style={{
              padding: '0.6rem 1rem',
              cursor: hasChildren ? 'default' : 'pointer',
              backgroundColor: isActive ? '#dc3545' : (isSubFlyoutOpen ? 'rgba(255,255,255,0.15)' : 'transparent'),
              color: isActive ? 'white' : ((!projectId && !doesNotNeedProject) ? '#777' : '#e0e0e0'),
              fontSize: '0.85rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem',
              transition: 'background-color 0.15s ease'
            }}
            onMouseEnter={(e) => {
              if (!isActive && !isSubFlyoutOpen) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
            }}
            onMouseLeave={(e) => {
              if (!isActive && !isSubFlyoutOpen) e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, overflow: 'hidden' }}>
              {item.icon && <span style={{ fontSize: '0.9rem' }}>{item.icon}</span>}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
              {item.isModal && <span style={{ fontSize: '0.7rem' }}>⬈</span>}
            </span>
            {hasChildren && (
              <span style={{ 
                fontSize: '0.7rem',
                flexShrink: 0
              }}>▶</span>
            )}
          </div>
        </div>
      );
    });
  };

  // Mobile sidebar styles
  const mobileStyles = (isMobile || isTablet) ? {
    position: 'fixed',
    left: 0,
    top: 'var(--header-height, 56px)',
    width: '280px',
    maxWidth: '85vw',
    height: 'calc(100vh - var(--header-height, 56px))',
    transform: isMobileOpen ? 'translateX(0)' : 'translateX(-100%)',
    transition: 'transform 0.3s ease',
    zIndex: 'var(--z-sidebar, 100)',
    boxShadow: isMobileOpen ? '4px 0 20px rgba(0, 0, 0, 0.3)' : 'none',
  } : {};

  // Handle menu item click - if no project selected, navigate to projects page
  const handleMenuItemClick = (itemId) => {
    // Special handling for Department selection
    if (itemId.startsWith('dept-')) {
      const deptMap = {
        'dept-projects': 'projects',
        'dept-accounting': 'accounting',
        'dept-estimation': 'estimation',
        'dept-procurement': 'procurement',
        'dept-resources': 'resources',
        'dept-hse': 'hse',
        'dept-pmo': 'pmo'
      };
      const deptId = deptMap[itemId];
      if (deptId) {
        // Call the onSelectDepartment handler if provided
        if (onSelectDepartment) {
          onSelectDepartment(deptId);
        }
        // Also navigate to home to show the new department's menu
        navigate('/');
      }
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }
    
    // Special handling for My Projects - always navigate to /projects
    if (itemId === 'my-projects') {
      navigate('/projects');
      // Close admin panel if open
      window.dispatchEvent(new CustomEvent('closeAdminPanel'));
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }
    
    // Special handling for Project Directory - always navigate to /project-directory
    if (itemId === 'project-directory') {
      navigate('/project-directory');
      // Close admin panel if open
      window.dispatchEvent(new CustomEvent('closeAdminPanel'));
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }
    
    // Special handling for Contractors - always navigate to /contractors
    // Contractors is a company-wide feature, not project-specific.
    // (Suppliers live on the same screen, toggled via an in-page dropdown.)
    if (itemId === 'procurement-selected-contractors') {
      // Pass state so ContractorsPage knows user came from a project
      navigate('/contractors', { 
        state: { 
          fromProject: !!projectId, 
          projectId: projectId 
        } 
      });
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }
    
    // Resource Management
    if (itemId === 'resources-management' || itemId === 'resources') {
      onMenuItemClick('resources-management');
      navigate('/resource-management');
      if (isMobile || isTablet) onMobileClose?.();
      return;
    }
    if (itemId === 'resources-fabrication') {
      onMenuItemClick('resources-fabrication');
      navigate('/fabrication-histogram');
      if (isMobile || isTablet) onMobileClose?.();
      return;
    }
    if (itemId === 'resources-portfolio-evm') {
      onMenuItemClick('resources-portfolio-evm');
      navigate('/portfolio-evm');
      if (isMobile || isTablet) onMobileClose?.();
      return;
    }
    // Special handling for Time Tracker - navigate to standalone page
    // Handles both 'actuals' (parent) and 'actual-spent-hours' (legacy child ID)
    if (itemId === 'actuals' || itemId === 'actual-spent-hours') {
      navigate('/actual-spent', { 
        state: { 
          fromProject: !!projectId, 
          projectId: projectId 
        } 
      });
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }
    
    // Special handling for Coding Structure - navigate to standalone page
    if (itemId === 'coding-structure') {
      navigate('/coding-structure');
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }

    // Hiring / Layoff master employee list (Accounting)
    if (itemId === 'hiring-layoff') {
      navigate('/hiring-layoff');
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }

    // Material Management dashboard (Procurement master; Estimation/PM entries
    // open the same page — users scope it with the estimate/job filters).
    if (itemId.startsWith('material-management')) {
      navigate('/material-management');
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }
    
    // Special handling for Estimation items - navigate to standalone estimation page
    // Includes items that don't follow the estimation- prefix pattern
    const estimationItems = ['estimation', 'mto', 'material-index', 'shop-field-ratio', 'cost-breakdown', 'sov',
      'estimate-log', 'estimate-sharepoint', 'estimate-autodesk-build', 'estimate-procore', 'cost-breakdown-summary', 'cost-breakdown-disciplines', 'cost-breakdown-indirects',
      'cost-breakdown-site-expenses', 'cost-breakdown-rentals', 'cost-breakdown-labour-rates',
      'cost-breakdown-markup', 'cost-breakdown-bid-form', 'cost-breakdown-clarifications',
      'cost-breakdown-proposal-letter', 'cost-breakdown-llm-clarification-letter', 'currency-exchange'];
    if (itemId.startsWith('estimation-') || estimationItems.includes(itemId)) {
      navigate('/estimation', { 
        state: { 
          menuItem: itemId
        } 
      });
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }
    
    // Special handling for HSE (Safety) items - navigate to standalone page
    if (itemId.startsWith('safety-') || itemId === 'safety' || itemId === 'hse-report') {
      navigate('/hse', { 
        state: { 
          menuItem: itemId
        } 
      });
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }
    
    // Special handling for Training items - navigate to standalone page
    if (itemId.startsWith('training-') || itemId === 'training') {
      navigate('/training', { 
        state: { 
          menuItem: itemId
        } 
      });
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }
    
    // Special handling for Processes items - navigate to standalone page
    if (itemId.startsWith('processes-') || itemId === 'processes' || itemId === 'lgl-portal-tour') {
      navigate('/processes', { 
        state: { 
          menuItem: itemId
        } 
      });
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }
    
    // Special handling for Development items (including proposals) - navigate to standalone page
    if (itemId === 'development-proposals' || itemId.startsWith('development-') || itemId === 'development' || itemId === 'debugging' ||
        itemId === 'code-refactoring' || itemId.startsWith('test-') || itemId === 'test' ||
        itemId.startsWith('template-') || itemId === 'templates' ||
        itemId.startsWith('modules-') || itemId === 'modules' ||
        itemId.startsWith('dashboard-') || itemId === 'dashboards' ||
        itemId === 'file-upload' ||
        itemId.startsWith('wip-') || itemId === 'wip') {
      navigate('/development', { 
        state: { 
          menuItem: itemId
        } 
      });
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }
    
    // Special handling for Sales items
    if (itemId === 'clients-database' || itemId === 'bid-log') {
      navigate('/sales', { 
        state: { menuItem: itemId }
      });
      if (isMobile || isTablet) { onMobileClose?.(); }
      return;
    }
    
    // Special handling for Roadmap items - navigate to standalone page
    if (itemId === 'roadmap-erp' || itemId === 'roadmap-swimlane' || itemId === 'roadmap-strategy' || itemId === 'pmo-usage') {
      navigate('/development', { 
        state: { 
          menuItem: itemId
        } 
      });
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }

    if (itemId === 'subcontractor-discovery') {
      navigate('/subcontractor-discovery');
      if (isMobile || isTablet) { onMobileClose?.(); }
      return;
    }

    if (itemId === 'download-app') {
      navigate('/download-app');
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }

    // Special handling for Quality items - navigate to standalone quality page
    if (itemId.startsWith('quality-') || itemId === 'quality') {
      navigate('/quality', {
        state: { menuItem: itemId }
      });
      if (isMobile || isTablet) { onMobileClose?.(); }
      return;
    }
    
    // Special handling for Piping - navigate to piping matrix tracker
    if (itemId === 'piping-insulation') {
      if (!projectId) {
        navigate('/projects');
        return;
      }
      onMenuItemClick('piping-insulation');
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }
    
    // Special handling for Home on landing page - just stay on home
    if (itemId === 'home' && !projectId) {
      navigate('/');
      // Close admin panel if open
      window.dispatchEvent(new CustomEvent('closeAdminPanel'));
      if (isMobile || isTablet) {
        onMobileClose?.();
      }
      return;
    }
    
    if (!projectId) {
      // No project selected - navigate to project selection
      // (Items with standalone routes are handled above)
      navigate('/projects');
      return;
    }
    onMenuItemClick(itemId);
    if (isMobile || isTablet) {
      onMobileClose?.();
    }
  };

  menuNavigateHandlerRef.current = handleMenuItemClick;

  return (
    <>
      {/* Hide scrollbar CSS for webkit browsers */}
      <style>
        {`
          .sidebar-scroll-hidden::-webkit-scrollbar {
            width: 0;
            height: 0;
            display: none;
          }
        `}
      </style>
      
      {/* Backdrop for mobile */}
      {(isMobile || isTablet) && isMobileOpen && (
        <div
          onClick={onMobileClose}
          style={{
            position: 'fixed',
            top: 'var(--header-height, 56px)',
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99,
            animation: 'fadeIn 0.2s ease',
          }}
        />
      )}
      
      <div 
        ref={sidebarRef}
        className="no-print project-sidebar sidebar-scroll-hidden"
        data-tutorial="project-sidebar"
        style={{
          width: isCollapsed ? '56px' : `${sidebarWidth}px`,
          minWidth: isCollapsed ? '56px' : `${sidebarWidth}px`,
          height: '100%',
          backgroundColor: '#1a1a1a',
          borderRight: 'none',
          overflowY: 'auto',
          overflowX: 'hidden',
          transition: isResizing ? 'none' : 'width 0.2s ease, min-width 0.2s ease, transform 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          // Hide scrollbar but keep functionality (overlay style)
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
          // Apply mobile styles
          ...mobileStyles,
        }}
      >
      {/* Resize Handle */}
      {!isCollapsed && (
        <div
          onMouseDown={handleMouseDown}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '5px',
            height: '100%',
            cursor: 'col-resize',
            backgroundColor: isResizing ? '#dc3545' : '#333',
            transition: 'background-color 0.2s',
            zIndex: 10
          }}
          onMouseEnter={(e) => {
            if (!isResizing) e.currentTarget.style.backgroundColor = '#dc3545';
          }}
          onMouseLeave={(e) => {
            if (!isResizing) e.currentTarget.style.backgroundColor = '#333';
          }}
        />
      )}
      
      {/* Collapse Toggle - Hide on mobile */}
      {!(isMobile || isTablet) && (
        <div 
          onClick={onToggleCollapse}
          style={{
            padding: '0.75rem',
            cursor: 'pointer',
            borderBottom: '1px solid #333',
            textAlign: 'center',
            color: '#999',
            fontSize: '0.9rem'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          {isCollapsed ? '☰' : '◀ Collapse'}
        </div>
      )}
      
      {/* Mobile header with close button */}
      {(isMobile || isTablet) && (
        <div 
          style={{
            padding: '0.75rem 1rem',
            borderBottom: '1px solid #333',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ color: '#fff', fontWeight: '600', fontSize: '1rem' }}>Menu</span>
          <button
            onClick={onMobileClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#999',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '4px 8px',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Menu Sections */}
      {/* On mobile/tablet, always show full menu when sidebar is open (ignore isCollapsed) */}
      {(!isCollapsed || isMobile || isTablet) && (
        <div style={{ flex: 1 }}>
          {menuStructure.map(section => {
            const isExpanded = expandedSections[section.id];
            const isActive = isParentActive(section);

            const hasSubItems = section.subItems && section.subItems.length > 0;
            // My Projects can be expanded to show project list
            const canExpand = hasSubItems || section.id === 'my-projects';
            
            return (
              <div 
                key={section.id}
                ref={el => sectionRefs.current[section.id] = el}
              >
                {/* Section Header */}
                <div
                  data-tutorial={`${section.id}-menu`}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Items without expandable content - always navigate on click
                    if (!canExpand) {
                      handleMenuItemClick(section.id);
                      return;
                    }
                    // Items with expandable content - toggle on click
                    if (isMobile || isTablet) {
                      toggleSection(section.id);
                    } else {
                      // On desktop, clicking a section with subitems can also toggle it
                      toggleSection(section.id);
                    }
                  }}
                  style={{
                    padding: '0.75rem',
                    cursor: 'pointer',
                    backgroundColor: (isActive || (activeMenuItem === section.id)) ? 'rgba(220, 53, 69, 0.3)' : 'rgba(255,255,255,0.05)',
                    color: (isActive || (activeMenuItem === section.id)) ? '#ff6b7a' : '#fff',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    borderBottom: '1px solid #333'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive && activeMenuItem !== section.id) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive && activeMenuItem !== section.id) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                  }}
                >
                  <span>{section.icon}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {section.id === 'home' && projectId ? 'Project Home' : section.label}
                  </span>
                  {canExpand && (
                    <span style={{ 
                      fontSize: '0.7rem',
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s'
                    }}>▶</span>
                  )}
                </div>

                {/* Section Items */}
                {isExpanded && section.subItems && (
                  <div style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
                    {renderSubItems(section.subItems)}
                  </div>
                )}
                
                {/* My Projects Dropdown */}
                {isExpanded && section.id === 'my-projects' && (
                  <div style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '0.5rem 0' }}>
                    {/* Search Box */}
                    <div style={{ padding: '0.25rem 0.75rem 0.5rem' }}>
                      <input
                        type="text"
                        placeholder="Search job # or name..."
                        value={projectSearchTerm}
                        onChange={(e) => setProjectSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: '100%',
                          padding: '0.4rem 0.6rem',
                          fontSize: '0.8rem',
                          border: '1px solid #555',
                          borderRadius: '4px',
                          backgroundColor: '#333',
                          color: '#fff',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#dc3545'}
                        onBlur={(e) => e.target.style.borderColor = '#555'}
                      />
                    </div>
                    {/* Project List */}
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {projectsLoading ? (
                        <div style={{ padding: '0.5rem 0.75rem', textAlign: 'center', color: '#888', fontSize: '0.8rem' }}>
                          Loading...
                        </div>
                      ) : filteredProjects.length === 0 ? (
                        <div style={{ padding: '0.5rem 0.75rem', textAlign: 'center', color: '#888', fontSize: '0.8rem' }}>
                          {projectSearchTerm ? 'No projects found' : 'No projects'}
                        </div>
                      ) : (
                        filteredProjects.slice(0, 10).map(project => (
                          <div
                            key={project.id}
                            onClick={() => {
                              setProjectSearchTerm('');
                              navigate(`/project/${project.id}`);
                            }}
                            style={{
                              padding: '0.4rem 0.75rem',
                              paddingLeft: '1.5rem',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              color: '#e0e0e0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              transition: 'background-color 0.15s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <span style={{ fontWeight: '600', color: '#dc3545', minWidth: '45px', fontSize: '0.75rem' }}>
                              {project.job_number}
                            </span>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                              {project.job_name}
                            </span>
                          </div>
                        ))
                      )}
                      {filteredProjects.length > 10 && (
                        <div 
                          onClick={() => {
                            window.dispatchEvent(new CustomEvent('closeAdminPanel'));
                            navigate('/projects');
                          }}
                          style={{ padding: '0.4rem 0.75rem', paddingLeft: '1.5rem', color: '#dc3545', fontSize: '0.75rem', cursor: 'pointer' }}
                          onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                        >
                          +{filteredProjects.length - 10} more...
                        </div>
                      )}
                    </div>
                    {/* View All link */}
                    <div 
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('closeAdminPanel'));
                        navigate('/projects');
                      }}
                      style={{ 
                        padding: '0.5rem 0.75rem',
                        paddingLeft: '1.5rem',
                        color: '#dc3545',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        borderTop: '1px solid #444',
                        marginTop: '0.25rem'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.15)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      View All Projects →
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Collapsed Icons - Hide on mobile */}
      {isCollapsed && !(isMobile || isTablet) && (
        <div style={{ flex: 1, paddingTop: '0.5rem' }}>
          {menuStructure.map(section => {
            const hasSubItems = section.subItems && section.subItems.length > 0;
            // My Projects should also show flyout with project list
            const showsFlyout = hasSubItems || section.id === 'my-projects';
            
            return (
              <div
                key={section.id}
                onClick={(e) => {
                  e.stopPropagation();
                  // For items without flyout (like Home), navigate directly
                  if (!showsFlyout) {
                    handleMenuItemClick(section.id);
                  }
                  // For items with flyout (including My Projects), clicking also shows the flyout
                  else {
                    handleCollapsedIconMouseEnter(section, e);
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                  if (showsFlyout) {
                    handleCollapsedIconMouseEnter(section, e);
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  if (showsFlyout) {
                    handleCollapsedIconMouseLeave();
                  }
                }}
                style={{
                  width: '56px',
                  height: '56px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  color: isParentActive(section) || activeMenuItem === section.id ? '#ff6b7a' : '#999',
                  transition: 'background-color 0.15s ease'
                }}
                title={!showsFlyout ? (section.id === 'home' && projectId ? 'Project Home' : section.label) : undefined}
              >
                {section.icon}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Flyout Menu for Collapsed Sidebar */}
      {isCollapsed && hoveredSection && !(isMobile || isTablet) && (
        <div
          onMouseEnter={handleFlyoutMouseEnter}
          onMouseLeave={handleFlyoutMouseLeave}
          style={{
            position: 'fixed',
            left: '56px',
            top: flyoutPosition.top,
            minWidth: '200px',
            maxHeight: flyoutPosition.maxHeight || 'calc(100vh - 100px)',
            overflowY: 'auto',
            backgroundColor: '#2a2a2a',
            borderRadius: '0 8px 8px 0',
            boxShadow: '4px 4px 16px rgba(0, 0, 0, 0.4)',
            zIndex: 1000,
          }}
        >
          {/* Flyout Header */}
          <div style={{
            padding: '0.75rem 1rem',
            backgroundColor: '#333',
            borderBottom: '1px solid #444',
            fontWeight: '600',
            fontSize: '0.75rem',
            color: '#999',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span>{hoveredSection.icon}</span>
            <span>{hoveredSection.label}</span>
          </div>
          
          {/* Project Selector - show when no project selected AND section needs a project (not hideInProject) */}
          {((!projectId && !hoveredSection.hideInProject) || hoveredSection.id === 'my-projects') && (
            <div style={{ borderBottom: hoveredSection.id === 'my-projects' ? 'none' : '1px solid #444' }}>
              {/* Header - only show "Select a project" when not on My Projects flyout */}
              {hoveredSection.id !== 'my-projects' && (
                <div style={{
                  padding: '0.5rem 0.75rem',
                  backgroundColor: 'rgba(220, 53, 69, 0.15)',
                  fontSize: '0.75rem',
                  color: '#ff6b7a',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span>📋</span>
                  <span>Select a project:</span>
                </div>
              )}
              
              {/* Search Box */}
              <div style={{ padding: '0.5rem 0.75rem' }}>
                <input
                  type="text"
                  placeholder="Search job # or name..."
                  value={projectSearchTerm}
                  onChange={(e) => setProjectSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: '100%',
                    padding: '0.4rem 0.6rem',
                    fontSize: '0.8rem',
                    border: '1px solid #555',
                    borderRadius: '4px',
                    backgroundColor: '#333',
                    color: '#fff',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#dc3545'}
                  onBlur={(e) => e.target.style.borderColor = '#555'}
                />
              </div>
              
              {/* Project List */}
              <div style={{ 
                maxHeight: '200px', 
                overflowY: 'auto',
                padding: '0 0 0.5rem 0'
              }}>
                {projectsLoading ? (
                  <div style={{ padding: '0.75rem', textAlign: 'center', color: '#888', fontSize: '0.8rem' }}>
                    Loading projects...
                  </div>
                ) : filteredProjects.length === 0 ? (
                  <div style={{ padding: '0.75rem', textAlign: 'center', color: '#888', fontSize: '0.8rem' }}>
                    {projectSearchTerm ? 'No projects found' : 'No projects available'}
                  </div>
                ) : (
                  filteredProjects.slice(0, 10).map(project => (
                    <div
                      key={project.id}
                      onClick={() => {
                        setHoveredSection(null);
                        setProjectSearchTerm('');
                        navigate(`/project/${project.id}`);
                      }}
                      style={{
                        padding: '0.5rem 0.75rem',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        color: '#e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'background-color 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <span style={{ 
                        fontWeight: '600', 
                        color: '#dc3545',
                        minWidth: '56px'
                      }}>
                        {project.job_number}
                      </span>
                      <span style={{ 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        flex: 1
                      }}>
                        {project.job_name}
                      </span>
                    </div>
                  ))
                )}
                {filteredProjects.length > 10 && (
                  <div 
                    onClick={() => {
                      setHoveredSection(null);
                      navigate('/projects');
                    }}
                    style={{ 
                      padding: '0.5rem 0.75rem', 
                      textAlign: 'center', 
                      color: '#dc3545', 
                      fontSize: '0.75rem',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                  >
                    +{filteredProjects.length - 10} more → View all projects
                  </div>
                )}
                {/* View All Projects link for My Projects flyout */}
                {hoveredSection.id === 'my-projects' && (
                  <div 
                    onClick={() => {
                      setHoveredSection(null);
                      window.dispatchEvent(new CustomEvent('closeAdminPanel'));
                      navigate('/projects');
                    }}
                    style={{ 
                      padding: '0.75rem', 
                      textAlign: 'center', 
                      color: '#dc3545', 
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      borderTop: '1px solid #444',
                      marginTop: '0.5rem'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.15)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    View All Projects →
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Flyout Items */}
          <div style={{ padding: '0.5rem 0', maxHeight: '60vh', overflowY: 'auto' }}>
            {hoveredSection.subItems && renderFlyoutItems(hoveredSection.subItems, 0, [], hoveredSection.hideInProject)}
          </div>
        </div>
      )}
      
      {/* Sub-flyout for collapsed sidebar 3rd-level items (appears to the right) */}
      {isCollapsed && hoveredSection && collapsedSubFlyout && !(isMobile || isTablet) && (() => {
        const subItem = collapsedSubFlyout.item;
        if (!subItem || !subItem.subItems) return null;

        return (
          <div
            onMouseEnter={() => {
              clearAllFlyoutTimeouts();
            }}
            onMouseLeave={() => {
              collapsedSubFlyoutTimeout.current = setTimeout(() => {
                setCollapsedSubFlyout(null);
              }, 300);
            }}
            style={{
              position: 'fixed',
              top: collapsedSubFlyout.top,
              left: collapsedSubFlyout.left,
              minWidth: '180px',
              backgroundColor: '#2a2a2a',
              borderRadius: '0 8px 8px 0',
              boxShadow: '4px 4px 16px rgba(0, 0, 0, 0.4)',
              zIndex: 1001,
              padding: '0.25rem 0',
              maxHeight: '60vh',
              overflowY: 'auto',
            }}
          >
            <div style={{
              padding: '0.5rem 0.75rem',
              fontSize: '0.7rem',
              color: '#999',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              marginBottom: '0.25rem'
            }}>
              {subItem.label}
            </div>
            {subItem.subItems.map(child => {
              const childActive = isItemActive(child.id);
              return (
                <div
                  key={child.id}
                  onClick={() => {
                    handleMenuItemClick(child.id);
                    setHoveredSection(null);
                    setHoveredFlyoutItems({});
                    setCollapsedSubFlyout(null);
                  }}
                  style={{
                    padding: '0.5rem 0.75rem',
                    cursor: 'pointer',
                    backgroundColor: childActive ? '#dc3545' : 'transparent',
                    color: childActive ? 'white' : '#e0e0e0',
                    fontSize: '0.85rem',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    clearAllFlyoutTimeouts();
                    if (!childActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    if (!childActive) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {child.label}
                  {child.isModal && <span style={{ fontSize: '0.7rem', marginLeft: '0.25rem' }}>⬈</span>}
                </div>
              );
            })}
          </div>
        );
      })()}
      
      {/* Admin Panel - Bottom of sidebar (only for users with admin access) */}
      {userPermissions?.canAccessAdminPanel && (
        <div style={{ 
          marginTop: 'auto',
          borderTop: '1px solid #333',
          flexShrink: 0
        }}>
          {!isCollapsed ? (
            <div
              onClick={() => {
                navigate('/admin');
                if (isMobile || isTablet) {
                  onMobileClose?.();
                }
              }}
              style={{
                padding: '0.75rem',
                cursor: 'pointer',
                backgroundColor: isAdminActive ? 'rgba(139, 69, 19, 0.4)' : 'rgba(139, 69, 19, 0.2)',
                color: '#d4a574',
                fontWeight: '600',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'background-color 0.15s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(139, 69, 19, 0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(139, 69, 19, 0.2)'}
            >
              <span>⚡</span>
              <span>Admin Panel</span>
            </div>
          ) : !(isMobile || isTablet) && (
            <div
              onClick={() => {
                navigate('/admin');
              }}
              title="Admin Panel"
              style={{
                width: '56px',
                height: '56px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '1.2rem',
                color: '#d4a574',
                backgroundColor: isAdminActive ? 'rgba(139, 69, 19, 0.4)' : 'transparent',
                transition: 'background-color 0.15s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(139, 69, 19, 0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isAdminActive ? 'rgba(139, 69, 19, 0.4)' : 'transparent'}
            >
              ⚡
            </div>
          )}
        </div>
      )}
      
      {/* Touch-friendly padding at bottom for mobile */}
      {(isMobile || isTablet) && (
        <div style={{ 
          padding: 'max(16px, env(safe-area-inset-bottom))',
          flexShrink: 0,
        }} />
      )}
    </div>
    
    {/* Fixed-position flyout for 3rd level sub-menu items (rendered outside sidebar overflow) */}
    {hoveredSubMenuItem && !isCollapsed && (() => {
      const findItemById = (items, id) => {
        for (const item of items) {
          if (item.id === id) return item;
          if (item.subItems) {
            const found = findItemById(item.subItems, id);
            if (found) return found;
          }
        }
        return null;
      };
      const flyoutItem = findItemById(menuStructure, hoveredSubMenuItem);
      if (!flyoutItem || !flyoutItem.subItems) return null;

      return (
        <div
          style={{
            position: 'fixed',
            top: subMenuFlyoutPos.top,
            left: subMenuFlyoutPos.left,
            minWidth: '180px',
            backgroundColor: '#1e1e2d',
            borderRadius: '4px',
            boxShadow: '4px 4px 16px rgba(0,0,0,0.4)',
            border: '1px solid rgba(255,255,255,0.1)',
            zIndex: 1100,
            padding: '0.25rem 0',
            maxHeight: '60vh',
            overflowY: 'auto'
          }}
          onMouseEnter={() => {
            if (subMenuHoverTimeout.current) {
              clearTimeout(subMenuHoverTimeout.current);
              subMenuHoverTimeout.current = null;
            }
          }}
          onMouseLeave={() => {
            subMenuHoverTimeout.current = setTimeout(() => {
              setHoveredSubMenuItem(null);
            }, 250);
          }}
        >
          <div style={{
            padding: '0.4rem 0.75rem',
            fontSize: '0.7rem',
            color: '#999',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            marginBottom: '0.25rem'
          }}>
            {flyoutItem.label}
          </div>
          {flyoutItem.subItems.map(child => {
            const childActive = isItemActive(child.id);
            const childHasChildren = child.subItems && child.subItems.length > 0;
            return (
              <div key={child.id}>
                {childHasChildren ? (
                  <>
                    <div style={{
                      padding: '0.5rem 0.75rem',
                      fontSize: '0.7rem',
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginTop: '0.35rem',
                      borderTop: '1px solid rgba(255,255,255,0.06)'
                    }}>
                      {child.label}
                    </div>
                    {child.subItems.map(grandchild => {
                      const gcActive = isItemActive(grandchild.id);
                      return (
                        <div
                          key={grandchild.id}
                          onClick={() => {
                            handleMenuItemClick(grandchild.id);
                            setHoveredSubMenuItem(null);
                          }}
                          style={{
                            padding: '0.45rem 0.75rem 0.45rem 1.25rem',
                            cursor: 'pointer',
                            backgroundColor: gcActive ? '#dc3545' : 'transparent',
                            color: gcActive ? 'white' : '#e0e0e0',
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!gcActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                          }}
                          onMouseLeave={(e) => {
                            if (!gcActive) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          {grandchild.label}
                          {grandchild.isModal && <span style={{ fontSize: '0.7rem', marginLeft: '0.25rem' }}>⬈</span>}
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div
                    onClick={() => {
                      handleMenuItemClick(child.id);
                      setHoveredSubMenuItem(null);
                    }}
                    style={{
                      padding: '0.5rem 0.75rem',
                      cursor: 'pointer',
                      backgroundColor: childActive ? '#dc3545' : 'transparent',
                      color: childActive ? 'white' : '#e0e0e0',
                      fontSize: '0.85rem',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!childActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      if (!childActive) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {child.label}
                    {child.isModal && <span style={{ fontSize: '0.7rem', marginLeft: '0.25rem' }}>⬈</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    })()}
    </>
  );
});

export default ProjectSidebar;

